// Search filter state: one definition row per filter.
// Owns how each filter is parsed from / written to the URL hash, how it is
// sent to api.search, how it toggles and how it resets.
import voca from 'voca'
import { clone, filter, isEmpty } from 'underscore'

const identity = (value) => value

// Order matters: it is the order filters are written to the hash.
// modes: which api.search queries include the filter
//   "products" -> Search.load(), "new" -> Search.loadNewItems()
// clears: filters reset when this one is turned off
const FILTERS = [
  { key: "precortadas", flag: true, query: "precortadas", modes: ["products", "new"], clears: ["precortadas_collection"] },
  { key: "precortadas_collection", decode: decodeURI, query: "precortadas_collection", modes: ["products", "new"] },
  { key: "price", decode: identity, query: "price", modes: ["products"] },
  { key: "level1", decode: decodeURI, query: "level1_metadata", modes: ["products"] },
  { key: "level2", decode: decodeURI, query: "level2_metadata", modes: ["products"] },
  { key: "level3", decode: decodeURI, query: "level3_metadata", modes: ["products"] },
  { key: "tematica", decode: decodeURI, query: "tematica", modes: ["products"] },
  { key: "designer", decode: decodeURI, query: "designer_metadata", modes: ["products"] },
]

function findFilter(key) {
  const definition = FILTERS.find((item) => item.key === key)
  if (!definition) throw new Error(`Unknown search filter: ${key}`)
  return definition
}

function isActive(definition, value) {
  if (definition.flag) return value === true
  return !!value
}

export function emptyFilters() {
  const filters = {}
  for (const definition of FILTERS) {
    filters[definition.key] = definition.flag ? false : null
  }
  return filters
}

// Flags flip; value filters select, or clear when the same value is picked again.
export function toggleFilter(filters, key, value) {
  const definition = findFilter(key)
  const result = clone(filters)
  if (definition.flag) {
    result[key] = !filters[key]
    if (!result[key]) {
      for (const cleared of definition.clears || []) result[cleared] = null
    }
  } else {
    result[key] = filters[key] === value ? null : value
  }
  return result
}

// "#page:2/limit:16/precortadas:1" -> { page: 2, limit: 16, filters: { precortadas: true } }
// Only keys present in the hash are returned.
export function parseHash(hash) {
  const result = { filters: {} }
  if (isEmpty(hash)) return result

  const rawItems = voca.trimLeft(hash, "#").split("/")
  for (const rawItem of rawItems) {
    if (isEmpty(rawItem)) continue
    const [identifier, value] = rawItem.split(":")

    if (identifier === "page" || identifier === "limit") {
      result[identifier] = parseInt(value, 10)
      continue
    }

    const definition = FILTERS.find((item) => item.key === identifier)
    if (!definition) continue
    result.filters[identifier] = definition.flag
      ? parseInt(value, 10) === 1
      : definition.decode(value)
  }
  return result
}

// Hash stack consumed by Search._hashStackToUrl: [{ type, value }]
export function toHashStack(page, limit, filters) {
  const stack = [
    { type: "page", value: page },
    { type: "limit", value: limit },
  ]
  for (const definition of FILTERS) {
    const value = filters[definition.key]
    if (!isActive(definition, value)) continue
    stack.push({
      type: definition.key,
      value: definition.flag ? "1" : `${value}`,
    })
  }
  return stack
}

// api.search post fields for the active filters of the given mode.
export function toQuery(filters, mode = "products") {
  const query = {}
  const definitions = filter(FILTERS, (item) => item.modes.includes(mode))
  for (const definition of definitions) {
    const value = filters[definition.key]
    if (!isActive(definition, value)) continue
    query[definition.query] = definition.flag ? "1" : value
  }
  return query
}

// Hash stack written when the URL has no hash yet.
export function defaultHashStack(urlbinding) {
  const stack = [
    { type: "page", value: 1 },
    { type: "limit", value: 16 },
  ]
  if (urlbinding) stack.push({ type: "urlbinding", value: urlbinding })
  return stack
}

// [{ type: "page", value: 2 }, { type: "limit", value: 16 }] -> "#page:2/limit:16"
export function hashFromStack(stack) {
  return "#" + stack.map((item) => `${item.type}:${item.value}`).join("/")
}
